import useCrossTabState from '~/hooks/useCrossTabState';
import useTimeoutToggler from '~/hooks/useTimeoutToggler';
import { useEffect, useCallback, useRef } from 'react';
import ValidationService from '~/services/ValidationService';
import useSession from '~/hooks/useSession/useSession';
import useNotifications from '~/context/Notifications';
import useSessionPlQuery from '~/hooks/queries/useSessionPlQuery';

function SessionTimer() {
  const [warningTimeout, resetWarningTimeout] = useTimeoutToggler(1000 * 60 * 45); // 45mins
  const [logoutTimeout, resetLogoutTimeout] = useTimeoutToggler(1000 * 60 * 59); // 59mins
  const hasShownWarningRef = useRef(false);
  const [sessionPlTracker] = useCrossTabState('sessionPlTracker', 0);
  const sessionPlQuery = useSessionPlQuery((data) => ({
    accountId: data.id,
    accountType: data.accountType,
  }));
  const { setNotification, closeNotification } = useNotifications();
  const { logout } = useSession();

  const handleStayLoggedIn = useCallback(async () => {
    if (!sessionPlQuery.data) {
      logout();
      return;
    }
    const sessionInfo = await ValidationService.checkClientSession(sessionPlQuery.data);
    if (!sessionInfo || ('error' in sessionInfo && sessionInfo.error)) {
      logout();
    } else {
      closeNotification();
    }
  }, [sessionPlQuery.data, logout, closeNotification]);

  useEffect(() => {
    if (warningTimeout && !hasShownWarningRef.current) {
      hasShownWarningRef.current = true;
      // 15 mins to go before logout
      setNotification(
        true,
        'warning',
        'You seem to have been idle for a while',
        'top-center',
        handleStayLoggedIn,
        'Stay logged in',
      );
    }
  }, [warningTimeout, setNotification, handleStayLoggedIn]);

  useEffect(() => {
    if (logoutTimeout) {
      logout(0);
    }
  }, [logout, logoutTimeout]);

  useEffect(() => {
    // when sessionStorage changes, call reset timeouts
    resetLogoutTimeout();
    resetWarningTimeout();
  }, [resetLogoutTimeout, resetWarningTimeout, sessionPlTracker]);

  return null;
}

export default SessionTimer;
